/**
 * The admin client for Settings → Rewards → Referral message
 * (`GET/PUT /api/v2/admin/tenant/referral-share`).
 *
 * The READ is a raw fetch on purpose, the never-sign-out pattern of
 * `loadBookingCareSettings`: frontends auto-deploy before the server, and an
 * older server answers this path with 403 `route_not_mapped`. That, a 404, or
 * a role 403 is `absent` and the card renders nothing. Only a 5xx or a network
 * failure is a real error with Retry.
 */
import type { ReferralShareOption } from '@ferro/config';
import { api, humanizeApiError, readSession, type ApiResponse } from './api.js';

export const REFERRAL_SHARE_PATH = '/api/v2/admin/tenant/referral-share';

export interface TenantReferralShareSettings {
  /** The saved wording; null = today's message, the first option. */
  message: string | null;
  options: ReferralShareOption[];
  customerOrigin: string | null;
  limits: { maxMessageLength: number };
  lastChange: { at: string; byName: string } | null;
}

export type ReferralShareSaveResponse = TenantReferralShareSettings & {
  changed: boolean;
  audited: boolean;
};

export type ReferralShareLoad =
  | { kind: 'ok'; data: TenantReferralShareSettings }
  /** Older server (route_not_mapped), no config row, or a role without read: render nothing. */
  | { kind: 'absent' }
  | { kind: 'error'; message: string };

export const REFERRAL_SHARE_LOAD_ERROR = "We couldn't load the referral message.";

export async function loadReferralShareSettings(apiUrl: string): Promise<ReferralShareLoad> {
  const { token } = readSession();
  let res: Response;
  try {
    res = await fetch(`${apiUrl}${REFERRAL_SHARE_PATH}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch {
    return { kind: 'error', message: REFERRAL_SHARE_LOAD_ERROR };
  }
  if (res.status === 403 || res.status === 404) return { kind: 'absent' };
  if (!res.ok) return { kind: 'error', message: REFERRAL_SHARE_LOAD_ERROR };
  try {
    const data = (await res.json()) as TenantReferralShareSettings;
    if (!data || typeof data !== 'object' || !Array.isArray(data.options)) {
      return { kind: 'error', message: REFERRAL_SHARE_LOAD_ERROR };
    }
    return { kind: 'ok', data };
  } catch {
    return { kind: 'error', message: REFERRAL_SHARE_LOAD_ERROR };
  }
}

export function saveReferralShareSettings(
  apiUrl: string,
  message: string | null,
): Promise<ApiResponse<ReferralShareSaveResponse>> {
  return api.put<ReferralShareSaveResponse>(apiUrl, REFERRAL_SHARE_PATH, { message });
}

/** The server's sentence from a 400, when it sent one for the message field. */
export function messageErrorOf(details: unknown): string | null {
  if (!Array.isArray(details)) return null;
  for (const d of details) {
    if (!d || typeof d !== 'object') continue;
    const message = (d as { message?: unknown }).message;
    if (typeof message === 'string') return humanizeApiError(message);
  }
  return null;
}
